/**
 * Resoconti di "Scrivi a piu' persone": elenco degli invii dell'organizzazione
 * e, per ciascuno, l'esito per destinatario (inviato, non inviato col motivo,
 * in attesa, annullato). Si possono annullare i messaggi non ancora partiti.
 */
import { useCallback, useEffect, useState } from 'react';
import { ArrowLeft, Loader2, RefreshCw } from 'lucide-react';
import { useApp } from '@/components/AppProvider';
import { tip } from '@/components/messages/ChatTooltip';
import {
  cancelRemaining, fetchBulkReport, listBulkSends, type BulkSendListItem,
} from '@/lib/services/whatsapp/bulkSend/bulkSendService';
import {
  outcomeOf, summarize, summaryLine, OUTCOME_LABELS, type BulkOutcome, type BulkReportRow,
} from '@/lib/services/whatsapp/bulkSend/bulkReport';

const OUTCOME_CLS: Record<BulkOutcome, string> = {
  inviato: 'text-emerald-700 dark:text-emerald-300',
  fallito: 'text-red-600 dark:text-red-300',
  in_attesa: 'text-[#787774]',
  annullato: 'text-[#787774] line-through',
};

const fmt = (iso: string) => new Date(iso).toLocaleString('it-IT', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });

export function BulkSendsReport({ organizationId, initialId }: { organizationId: string; initialId?: string | null }) {
  const { confirm } = useApp();
  const [items, setItems] = useState<BulkSendListItem[]>([]);
  const [openId, setOpenId] = useState<string | null>(initialId ?? null);
  const [rows, setRows] = useState<BulkReportRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const loadList = useCallback(async () => {
    setLoading(true); setError(null);
    try { setItems(await listBulkSends(organizationId)); } catch { setError('Non riesco a caricare gli invii.'); } finally { setLoading(false); }
  }, [organizationId]);

  const loadReport = useCallback(async (id: string) => {
    setLoading(true); setError(null);
    try { setRows(await fetchBulkReport(id)); } catch { setError('Non riesco a caricare il resoconto.'); } finally { setLoading(false); }
  }, []);

  useEffect(() => { void loadList(); }, [loadList]);
  useEffect(() => { if (openId) void loadReport(openId); }, [openId, loadReport]);

  const current = items.find((i) => i.id === openId);
  const channel = current?.channel ?? 'standard';
  const summary = summarize(rows, channel);

  const annulla = async () => {
    if (!openId) return;
    const ok = await confirm({ title: 'Annullare i messaggi non ancora partiti?', message: `${summary.in_attesa} messaggi in attesa. Quelli gia' inviati restano inviati.`, confirmText: 'Annulla i restanti', cancelText: 'Indietro', type: 'danger' });
    if (!ok) return;
    try { await cancelRemaining(openId); await loadReport(openId); } catch { setError("Annullamento non riuscito. Riprova."); }
  };

  if (!openId) {
    return (
      <div className="space-y-1">
        {loading && <p className="flex items-center gap-2 text-sm text-[#787774]"><Loader2 className="w-4 h-4 animate-spin" /> Carico...</p>}
        {error && <p className="text-sm text-red-600">{error}</p>}
        {!loading && items.length === 0 && <p className="text-sm text-[#787774] py-6 text-center">Nessun invio a piu' persone finora.</p>}
        {items.map((i) => (
          <button key={i.id} type="button" onClick={() => setOpenId(i.id)} className="w-full text-left px-3 py-2 rounded-md hover:bg-[rgba(55,53,47,0.04)] dark:hover:bg-white/5">
            <p className="text-sm text-[#37352F] dark:text-white truncate">{i.audience_label || 'Destinatari scelti'}</p>
            <p className="text-xs text-[#787774] truncate">
              {fmt(i.created_at)} · {i.total_recipients} destinatari · {i.channel === 'standard' ? 'Standard' : `Modello ${i.template_name}`}
            </p>
          </button>
        ))}
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <button type="button" onClick={() => { setOpenId(null); setRows([]); }} className="inline-flex items-center gap-1 text-xs text-[#2383E2] hover:underline">
          <ArrowLeft className="w-3.5 h-3.5" /> Tutti gli invii
        </button>
        <button type="button" onClick={() => loadReport(openId)} className="p-1 rounded hover:bg-[rgba(55,53,47,0.08)]" {...tip('Aggiorna il resoconto')} aria-label="Aggiorna">
          <RefreshCw className={`w-3.5 h-3.5 text-[#787774] ${loading ? 'animate-spin' : ''}`} />
        </button>
      </div>
      <p className="text-sm font-medium text-[#37352F] dark:text-white">{summaryLine(summary)}</p>
      {error && <p className="text-sm text-red-600">{error}</p>}
      <div className="max-h-[45vh] overflow-y-auto divide-y divide-[rgba(55,53,47,0.06)] dark:divide-white/5">
        {rows.map((r) => {
          const o = outcomeOf(r, channel);
          return (
            <div key={r.scheduled_id} className="flex items-start justify-between gap-3 py-1.5">
              <div className="min-w-0">
                <p className="text-sm text-[#37352F] dark:text-white truncate">{r.recipient_name || `+${r.chat_jid.split('@')[0]}`}</p>
                {o.reason && <p className="text-xs text-red-600 dark:text-red-300">{o.reason}</p>}
              </div>
              <span className={`text-xs flex-shrink-0 ${OUTCOME_CLS[o.outcome]}`} {...tip(o.outcome === 'in_attesa' ? `Previsto alle ${fmt(r.scheduled_at)}` : null)}>
                {OUTCOME_LABELS[o.outcome]}
              </span>
            </div>
          );
        })}
      </div>
      {summary.in_attesa > 0 && (
        <button type="button" onClick={annulla} className="text-xs text-red-600 hover:underline">Annulla i messaggi non ancora partiti</button>
      )}
    </div>
  );
}
